import { useMutation } from '@apollo/client';
import { SupportPolicy } from '@provision/entity-types';
import { useCallback } from 'react';
import {
  UPDATE_SUPPORT_POLICY_MUTATION,
  UPDATE_SUPPORT_POLICY_WITH_GROUPS_MUTATION,
} from 'src/graphql/entities/support-policy/support-policy.operations';
import { ConditionInput } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';

export type SupportPolicyWithNullableDates = Omit<
  SupportPolicy,
  'validUntil' | 'pinnedAt' | 'urgencyLevel' | 'escalationRules'
> & {
  validUntil: string | null;
  pinnedAt: string | null;
  urgencyLevel?: string | null;
  escalationRules?: string | null;
};

export interface UpdateSupportPolicyMutation {
  updateSupportPolicy: Partial<
    Omit<SupportPolicyWithNullableDates, 'partner' | 'createdBy'> & {
      __typename: string;
    }
  >[];
}

export type UpdateSupportPolicyMutationParams = {
  conditions: ConditionInput[];
  data: Partial<SupportPolicyWithNullableDates>;
};

export interface UpdateSupportPolicyWithGroupsMutation {
  updateSupportPolicyWithGroups: SupportPolicyWithNullableDates & { __typename: string };
}

export type UpdateSupportPolicyWithGroupsMutationParams = {
  data: {
    id: string;
    supportPolicy: Partial<SupportPolicyWithNullableDates>;
    userGroupIds: string[];
  };
};

export const useUpdateSupportPolicy = () => {
  const [updateSupportPolicyMutation, { loading: isUpdating }] = useMutation<
    UpdateSupportPolicyMutation,
    UpdateSupportPolicyMutationParams
  >(UPDATE_SUPPORT_POLICY_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.updateSupportPolicy?.length) return;
      const updateResult = data.updateSupportPolicy[0];
      const id = cache.identify({
        __typename: 'SupportPolicy',
        id: updateResult.id,
      });

      const fields = generateFieldModifiersMap(updateResult);

      cache.modify({
        id,
        fields,
      });
    },
  });

  const [updateSupportPolicyWithGroupsMutation] = useMutation<
    UpdateSupportPolicyWithGroupsMutation,
    UpdateSupportPolicyWithGroupsMutationParams
  >(UPDATE_SUPPORT_POLICY_WITH_GROUPS_MUTATION);

  // Simpler update, better to use for smaller changes
  const updateSupportPolicy = useCallback(
    async (
      id: string,
      partial: Partial<SupportPolicyWithNullableDates>,
      supportPolicy: SupportPolicyWithNullableDates,
    ) => {
      await updateSupportPolicyMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: String(id),
            },
          ],
          data: {
            ...partial,
          },
        },
        optimisticResponse: {
          updateSupportPolicy: [
            {
              ...supportPolicy,
              ...partial,
              __typename: 'SupportPolicy',
              id,
              updatedAt: new Date().toISOString(),
            },
          ],
        },
      });
    },
    [updateSupportPolicyMutation],
  );

  // Full update, with support of userGroups, more backend overhead
  const updateSupportPolicyWithGroups = useCallback(
    async ({
      id,
      partial,
      userGroupIds,
      supportPolicy,
    }: {
      id: string;
      partial: Partial<SupportPolicyWithNullableDates>;
      userGroupIds: string[];
      supportPolicy: SupportPolicyWithNullableDates;
    }) => {
      await updateSupportPolicyWithGroupsMutation({
        variables: {
          data: {
            id,
            supportPolicy: partial,
            userGroupIds,
          },
        },
        optimisticResponse: {
          updateSupportPolicyWithGroups: {
            ...supportPolicy,
            ...partial,
            // @ts-expect-error Ignoring error to avoid having to pass entire user group objects
            userGroups: userGroupIds.map((groupId) => ({
              id: groupId,
              __typename: 'UserGroup',
            })),
            __typename: 'SupportPolicy',
            id,
            updatedAt: new Date().toISOString(),
          },
        },
        update: (cache, { data }) => {
          if (!data?.updateSupportPolicyWithGroups) return;

          cache.modify({
            id: cache.identify({ __typename: 'SupportPolicy', id }),
            fields: {
              userGroups() {
                return data.updateSupportPolicyWithGroups.userGroups;
              },
              updatedAt() {
                return data.updateSupportPolicyWithGroups.updatedAt;
              },
            },
          });
        },
      });
    },
    [updateSupportPolicyWithGroupsMutation],
  );

  return {
    updateSupportPolicyWithGroups,
    updateSupportPolicy,
    isUpdating,
  };
};
